/**
 * Sidebar
 * Purpose: Left navigation with GLOBAL wordmark, nav items, and user footer.
 *
 * Supports a collapsed mode (icons-only, ~56px wide) so the main content area
 * can be wider on cramped screens. The collapse state is owned by App.jsx and
 * persisted to localStorage so it survives reloads.
 */
import { clsx } from 'clsx';
import { NAV } from '../../data/navigation';
import { GlobalLogo } from '../ui/GlobalLogo';
import { Icon } from '../ui/Icon';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { getRole } from '../../data/admins';
import { canSeeTab, roleLabel as labelOfRole } from '../../data/roles';
import { useMyAccess } from '../../hooks/useMyAccess';
import { TicketButton } from './TicketButton';

// Derive a display name from an email address.
// "john.doe@globalat.com"  -> "John Doe"
// "a.sharif@globalat.com"  -> "A Sharif"
// ponytail: cheap heuristic until we add a real users table with first/last name.
const nameFromEmail = (email) => {
  if (!email) return '';
  const local = email.split('@')[0] || '';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};
const initialsFromEmail = (email) => {
  const name = nameFromEmail(email);
  const parts = name.split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase();
};

export const Sidebar = ({ active, onNavigate, badges = {}, collapsed = false, onToggleCollapse }) => {
  const { user } = useAuth();
  const { users } = useApp();
  const role = getRole(user?.email, users);
  const access = useMyAccess();
  const displayName = nameFromEmail(user?.email) || 'Guest';
  const initials = initialsFromEmail(user?.email);
  const roleLabel = labelOfRole(role);
  // Nav visibility follows the person's EFFECTIVE access - their role plus
  // their own overrides (src/data/roles.js), live: a tab appears or goes the
  // moment someone changes their access.
  const navItems = NAV.filter((n) => canSeeTab(access, n.id));

  return (
    <aside
      className={clsx(
        'h-screen bg-gat-greenDarkest flex flex-col shrink-0 border-r border-white/5 relative transition-[width] duration-200',
        collapsed ? 'w-14 px-2 py-3' : 'w-60 px-4 py-5'
      )}
    >
      {/* COLLAPSE TOGGLE — floating arrow that hangs off the right edge of the sidebar */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-7 w-6 h-6 rounded-full bg-white text-gat-greenDarkest border border-gat-greenDarkest shadow-md flex items-center justify-center z-20 hover:bg-gat-greenBright hover:text-gat-greenDarkest transition-colors"
        >
          <span className="text-[10px] font-black leading-none" style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
            ▶
          </span>
        </button>
      )}

      {/* LOGO — full wordmark when expanded, mini leaf icon when collapsed */}
      <div className={clsx('mb-6', collapsed ? 'px-0 flex justify-center' : 'px-1')}>
        {collapsed ? (
          <div className="w-9 h-9 rounded-md bg-gat-greenBright/15 flex items-center justify-center text-gat-greenBright text-base font-black">
            G
          </div>
        ) : (
          <GlobalLogo className="w-full max-w-[190px] h-auto" />
        )}
      </div>

      {/* NAV */}
      {!collapsed && <div className="text-eyebrow uppercase text-white/45 px-2 pb-2">Workspace</div>}
      <nav className="flex-1">
        {navItems.map((n) => {
          const isActive = active === n.id;
          const badge = badges[n.id];
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => onNavigate?.(n.id)}
              title={collapsed ? n.label : undefined}
              className={clsx(
                'group w-full flex items-center rounded text-[13px] font-medium transition-colors mb-px',
                collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2',
                isActive ? 'bg-gat-greenBright/20 text-white font-semibold' : 'text-white/60 hover:bg-white/5 hover:text-white'
              )}
            >
              {collapsed ? (
                <Icon name={n.icon} size={18} color={isActive ? '#A7D7A0' : 'currentColor'} />
              ) : (
                <>
                  <span className={clsx('w-1.5 h-1.5 rounded-full', isActive ? 'bg-gat-greenBright' : 'bg-current opacity-50')} />
                  <span className="flex-1 text-left">{n.label}</span>
                  {badge != null && badge > 0 && (
                    <span
                      className={clsx(
                        'text-[10px] px-1.5 py-px rounded-full font-semibold min-w-[20px] text-center',
                        isActive ? 'bg-gat-greenBright text-gat-greenDarkest' : 'bg-white/10 text-white'
                      )}
                    >
                      {badge}
                    </span>
                  )}
                </>
              )}
              {collapsed && badge != null && badge > 0 && (
                <span className="absolute mt-[-12px] ml-3.5 w-1.5 h-1.5 rounded-full bg-gat-greenBright" />
              )}
            </button>
          );
        })}
      </nav>

      <TicketButton collapsed={collapsed} page={NAV.find((n) => n.id === active)?.label || ''} />

      {/* USER FOOTER */}
      <div className={clsx('border-t border-white/10 pt-3.5', collapsed ? 'px-0 flex justify-center' : 'px-2 flex items-center gap-2.5')}>
        <div className="w-8 h-8 rounded-md bg-gradient-to-br from-gat-greenDark to-gat-green flex items-center justify-center text-white text-[11px] font-extrabold shrink-0">
          {initials}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold text-white truncate">{displayName}</div>
            <div className="text-[10px] text-white/40 font-medium">{roleLabel}</div>
          </div>
        )}
      </div>
    </aside>
  );
};
